import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { SelectBox, DropDownBox, List } from "devextreme-react";
import DateBox from "devextreme-react/date-box";
import { LoadPanel } from "devextreme-react/load-panel";
import moment from "moment";
import { axiosGet, axiosPost } from "../../apis/useAxios.js";
import DataSource from "devextreme/data/data_source.js";
import DataGrid, {
  Column,
  Export,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";

export function AirportGeofenceReportComponent() {
  const [state, setState] = useState({
    isFilter: false,
    loadPanelVisible: false,
    filterFromDatetime: new Date(),
    filterToDatetime: new Date(),
    filterDropdownEmployeeDataSource: [],
    airportDataSource: [],
    airportGeofenceReportDataSource: [],
    airportCode: "",
    employeeNumber: "",
  });

  const airlineCode = sessionStorage.getItem("session_airlineCode");
  const refDropDownOperator = useRef(null);
  const refLstEmployees = useRef(null);

  const set = useCallback(
    (patch) => setState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const crewMemberDataSource = useMemo(
    () =>
      new DataSource({
        store: state.filterDropdownEmployeeDataSource,
        sort: "employeeName",
        paginate: true,
        pageSize: 10,
      }),
    [state.filterDropdownEmployeeDataSource],
  );

  const getAirports = useCallback(async () => {
    try {
      const res = await axiosGet(`Airport/get-airport-dropdown/${airlineCode}`);
      set({ airportDataSource: res?.payload ?? [] });
    } catch {
      // dropdown stays empty on error
    }
  }, [airlineCode, set]);

  const getCrewMember = useCallback(async () => {
    set({ loadPanelVisible: true });
    try {
      const res = await axiosGet(`/Crew/get-crew-dropdown`, {
        AirlineCode: airlineCode,
      });
      set({
        filterDropdownEmployeeDataSource: res?.payload ?? [],
        loadPanelVisible: false,
      });
    } catch {
      set({ loadPanelVisible: false });
    }
  }, [airlineCode, set]);

  useEffect(() => {
    const el = document.querySelector("#lblPageTitle");
    if (el) el.innerHTML = "Airport Geofence Report";
    Promise.all([getAirports(), getCrewMember()]);
  }, [getAirports, getCrewMember]);

  const getAirportGeofenceReport = useCallback(async () => {
    set({ isFilter: true });
    try {
      const response = await axiosPost("Report/GetAirportGeofenceReport", {
        AirlineCode: airlineCode,
        FromDate: moment(state.filterFromDatetime).format(
          "YYYY-MM-DD 00:00:00",
        ),
        ToDate: moment(state.filterToDatetime).format("YYYY-MM-DD 23:59:59"),
        AirportCode: state.airportCode,
        EmployeeNumber: state.employeeNumber,
      });
      set({
        airportGeofenceReportDataSource: response?.payload ?? [],
        isFilter: false,
      });
    } catch {
      set({ isFilter: false });
    }
  }, [
    airlineCode,
    state.filterFromDatetime,
    state.filterToDatetime,
    state.airportCode,
    state.employeeNumber,
    set,
  ]);

  const handleAirportChange = useCallback(
    (e) => set({ airportCode: e.selectedItem?.airportCode ?? "" }),
    [set],
  );

  const handleOnFilterPickupDateChanged = useCallback(
    (date) => set({ filterFromDatetime: date.value }),
    [set],
  );

  const handleOnFiltertoDateChanged = useCallback(
    (date) => set({ filterToDatetime: date.value }),
    [set],
  );

  const handleOnClearFilter = useCallback(() => {
    set({
      isFilter: false,
      loadPanelVisible: false,
      filterFromDatetime: new Date(),
      filterToDatetime: new Date(),
      airportGeofenceReportDataSource: [],
      airportCode: "",
      employeeNumber: "",
    });
  }, [set]);

  const setToday = useCallback(
    (e) => {
      e.preventDefault();
      set({
        filterFromDatetime: moment().startOf("day").toDate(),
        filterToDatetime: moment().endOf("day").toDate(),
      });
    },
    [set],
  );

  const setYesterday = useCallback(
    (e) => {
      e.preventDefault();
      set({
        filterFromDatetime: moment().subtract(1, "day").startOf("day").toDate(),
        filterToDatetime: moment().subtract(1, "day").endOf("day").toDate(),
      });
    },
    [set],
  );

  const setLast7days = useCallback(
    (e) => {
      e.preventDefault();
      set({
        filterFromDatetime: moment().subtract(6, "day").startOf("day").toDate(),
        filterToDatetime: moment().endOf("day").toDate(),
      });
    },
    [set],
  );

  const renderEmployeeItem = useCallback(
    (item) => (
      <p className="mb-0">
        {item.employeeName}
        <span className="text-muted">
          {item?.employeeNumber && (
            <>
              <br />
              <small style={{ fontSize: "11px" }}>
                <i className="fas fa-user" />
                {` ${item.employeeNumber}`}
              </small>
            </>
          )}
        </span>
      </p>
    ),
    [],
  );

  const renderEmployeeList = useCallback(
    () => (
      <List
        ref={refLstEmployees}
        dataSource={crewMemberDataSource}
        height={400}
        keyExpr="employeeNumber"
        displayExpr="employeeFullName"
        searchEnabled={true}
        searchExpr={["employeeNumber", "employeeName", "employeeFullName"]}
        searchMode="contains"
        pageLoadMode="scrollBottom"
        showSelectionControls={true}
        selectionMode="single"
        indicateLoading={true}
        selectByClick={true}
        onItemClick={(item) => {
          set({ employeeNumber: item?.itemData?.employeeNumber ?? "" });
          if (refDropDownOperator.current?.instance) {
            refDropDownOperator.current.instance.close();
          }
        }}
        itemRender={renderEmployeeItem}
      />
    ),
    [crewMemberDataSource, renderEmployeeItem, set],
  );

  return (
    <>
      <style>{`
        /* ── Airport Geofence Report — theme matching sidebar #8E6448 ── */
        .ca-filter-card {
          border: 1px solid #e8d5c4 !important;
          background: #fdf7f2 !important;
        }
        .ca-filter-body {
          padding: 0.75rem 1rem 0.5rem 1rem;
        }
        .ca-filter-body label {
          font-size: 0.72rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
          color: #7a4f2e !important;
          margin-bottom: 3px !important;
        }
        .ca-filter-body .quick-links a {
          font-size: 0.76rem;
          color: #8E6448;
          font-weight: 600;
          text-decoration: none;
        }
        .ca-filter-body .quick-links a:hover { text-decoration: underline; }
        .ca-grid-wrap .dx-header-row td {
          background-color: #fff0e6 !important;
          color: #000000 !important;
          font-size: 0.76rem !important;
          font-weight: 600 !important;
          letter-spacing: 0.02em !important;
          border-color: rgba(255,255,255,0.15) !important;
        }
        .ca-grid-wrap .dx-datagrid-filter-row td {
          background-color: #f5ede4 !important;
        }
        .ca-grid-wrap .dx-datagrid {
          border-radius: 8px !important;
          overflow: hidden !important;
          box-shadow: 0 2px 10px rgba(142,100,72,0.1) !important;
        }
        .ca-grid-wrap .dx-toolbar {
          background: #fdf7f2 !important;
          border-bottom: 1px solid #e8d5c4 !important;
          padding: 0.35rem 0.6rem !important;
        }
      `}</style>

      <div className="filter-box mb-0">
        <div
          className="card ca-filter-card p-0 m-0"
          style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
        >
          <div className="ca-filter-body">
            <div className="row">
              <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
                <div className="form-group">
                  <label>Airport</label>
                  <SelectBox
                    dataSource={state.airportDataSource}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="Select Airport"
                    name="airportCode"
                    value={state.airportCode}
                    displayExpr="airportName"
                    valueExpr="airportCode"
                    onSelectionChanged={handleAirportChange}
                    showClearButton={true}
                    searchEnabled={true}
                  />
                </div>
              </div>
              <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
                <div className="form-group">
                  <label>Employee</label>
                  <DropDownBox
                    ref={refDropDownOperator}
                    value={state.employeeNumber}
                    valueExpr="employeeNumber"
                    displayExpr="employeeFullName"
                    deferRendering={false}
                    showClearButton={true}
                    dataSource={state.filterDropdownEmployeeDataSource}
                    onValueChanged={() => {
                      if (refLstEmployees.current?.instance) {
                        refLstEmployees.current.instance.unselectAll();
                        refLstEmployees.current.instance.option(
                          "searchValue",
                          "",
                        );
                      }
                      set({ employeeNumber: "" });
                    }}
                    contentRender={renderEmployeeList}
                    placeholder="Select Employee"
                  />
                </div>
              </div>
              <div className="col-xs-3 col-sm-3 col-md-3 col-lg-3">
                <div className="form-group has-feedback">
                  <label>
                    Date & Time <span className="text-danger">*</span>
                  </label>
                  &nbsp;
                  <span className="quick-links">
                    <a href="#" onClick={setToday}>
                      <b>Today</b>
                    </a>
                  </span>
                  &nbsp;|&nbsp;
                  <span className="quick-links">
                    <a href="#" onClick={setYesterday}>
                      <b>Yesterday</b>
                    </a>
                  </span>
                  &nbsp;|&nbsp;
                  <span className="quick-links">
                    <a href="#" onClick={setLast7days}>
                      <b>Last 7 Days</b>
                    </a>
                  </span>
                  <div>
                    <DateBox
                      type="date"
                      className="replay-date-time"
                      name="filterFromDatetime"
                      placeholder="dd/MM/yyyy"
                      displayFormat="dd/MM/yyyy"
                      showAnalogClock={true}
                      useMaskBehavior={true}
                      defaultValue={state.filterFromDatetime}
                      value={state.filterFromDatetime}
                      onValueChanged={handleOnFilterPickupDateChanged}
                      width="100%"
                      acceptCustomValue={false}
                    />
                  </div>
                </div>
              </div>
              <div className="col-xs-3 col-sm-3 col-md-3 col-lg-3">
                <div className="form-group">
                  <label>To Date</label>
                  <DateBox
                    type="date"
                    className="replay-date-time"
                    name="filterToDatetime"
                    placeholder="dd/MM/yyyy"
                    displayFormat="dd/MM/yyyy"
                    showAnalogClock={true}
                    useMaskBehavior={true}
                    min={state.filterFromDatetime}
                    defaultValue={state.filterToDatetime}
                    value={state.filterToDatetime}
                    onValueChanged={handleOnFiltertoDateChanged}
                    width="100%"
                    acceptCustomValue={false}
                  />
                </div>
              </div>
              <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
                <div className="form-group has-feedback">
                  <div>
                    <br />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    onClick={getAirportGeofenceReport}
                  >
                    <i
                      className={`fas fa-${state.isFilter ? "spinner fa-spin" : "filter"}`}
                    />
                    &nbsp;Filter
                  </button>
                  &nbsp; &nbsp;
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleOnClearFilter}
                  >
                    <i className="fas fa-eraser" /> Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="card ca-grid-wrap mt-0"
        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      >
        <div className="card-body">
          <DataGrid
            dataSource={state.airportGeofenceReportDataSource}
            columnAutoWidth={true}
            showColumnLines={true}
            showRowLines={true}
            showBorders={true}
            allowColumnResizing={true}
            onCellPrepared={(e) => {
              if (e.rowType === "header") {
                e.cellElement.style.backgroundColor = "#fff0e6";
                e.cellElement.style.color = "#000000";
              }
            }}
          >
            <Column
              caption="Flight Info"
              dataField="flightInfo"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <Column
              caption="Flight Time"
              dataField="flightTime"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <Column
              caption="Reporting Time"
              dataField="reportingTime"
              allowSearch={true}
              allowFiltering={true}
              headerFilter={{ allowSearch: true }}
              allowSorting={true}
              dataType="datetime"
              format="MM/dd/yyyy HH:mm:ss"
            />
            <Column
              caption="Crew"
              dataField="crewName"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <Column
              caption="Rank"
              dataField="rank"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <Column
              caption="Vehicle No"
              dataField="vehicleNo"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <Column
              caption="Airport"
              dataField="airport"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <Column
              caption="Employee Number"
              dataField="employeeNumber"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <Column
              caption="Drop Time"
              dataField="dropTime"
              allowSearch={true}
              headerFilter={{ allowSearch: true }}
              allowFiltering={true}
              allowSorting={true}
            />
            <GroupPanel visible={false} />
            <HeaderFilter visible={true} />
            <SearchPanel
              visible={true}
              highlightCaseSensitive={true}
              width={240}
              placeholder="Search..."
            />
            <Grouping autoExpandAll={true} />
            <Paging defaultPageSize={10} />
            <Pager
              showPageSizeSelector={true}
              allowedPageSizes={[10, 20, 50]}
              showInfo={true}
              visible={true}
            />
            <Export
              enabled={true}
              allowExportSelectedData={false}
              fileName="Airport Geofence Report"
            />
          </DataGrid>
        </div>
      </div>

      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: "#historydiv" }}
        visible={state.loadPanelVisible}
        showIndicator={true}
        shading={true}
        showPane={true}
        closeOnOutsideClick={false}
      />
    </>
  );
}
